import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { EssayAnswerAttachment } from "@/types/agentic-chat";
import { ImagePlus, RefreshCw, Trash2 } from "lucide-react";

type EssayAttachmentPreviewProps = {
  attachment: EssayAnswerAttachment | null | undefined;
  onAdd: () => void;
  onReplace?: () => void;
  onRemove?: () => void;
  compact?: boolean;
};

export function EssayAttachmentPreview({
  attachment,
  onAdd,
  onReplace,
  onRemove,
  compact = false,
}: EssayAttachmentPreviewProps) {
  if (!attachment) {
    return (
      <Button
        type="button"
        variant="outline"
        className={cn(
          "h-9 rounded-full border-white/80 bg-white/88 px-3 text-[11px] text-slate-700 shadow-[0_10px_24px_rgba(148,163,184,0.1)]",
          compact && "h-8 px-2.5 text-[10px]",
        )}
        onClick={onAdd}
      >
        <ImagePlus className="mr-1.5 h-3.5 w-3.5" />
        Add photo
      </Button>
    );
  }

  const previewSrc = `data:${attachment.mimeType};base64,${attachment.imageBase64}`;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[0.95rem] border border-white/75 bg-white/88 px-2.5 py-2 shadow-[0_10px_24px_rgba(148,163,184,0.1)]">
      <img
        src={previewSrc}
        alt="Essay answer preview"
        className="h-14 w-14 rounded-[0.8rem] object-cover"
      />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold text-slate-800">One photo attached</p>
        <p className="mt-0.5 text-[10px] text-slate-500">
          {attachment.width ?? "?"} × {attachment.height ?? "?"}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <Button type="button" variant="ghost" size="sm" className="h-8 rounded-full px-2.5 text-[10px]" onClick={onReplace ?? onAdd}>
          <RefreshCw className="mr-1 h-3 w-3" />
          Replace
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-500" onClick={onRemove}>
          <Trash2 className="h-3.5 w-3.5" />
          <span className="sr-only">Remove photo</span>
        </Button>
      </div>
    </div>
  );
}
